/**
 * Agent Content Component - Main display area for the Agent node
 */

import React from 'react'
import { Bot, Play, Pause, Settings as SettingsIcon, Plus, Activity, Target, Image, Upload, DollarSign, Users, Database, Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { AgentNodeData, AgentState } from '../types'

interface AgentContentProps {
  data: AgentNodeData
  state: AgentState
  nodeStatus: 'initial' | 'loading' | 'error' | 'success'
  onCreateAgent: () => void
  onToggleAgent: () => void
  onShowSettings: () => void
  onShowLogs: () => void
  onShowUsage: () => void
  onShowCollaboration: () => void
  onShowQdrant: () => void
  onShowScheduling: () => void
  onShowManualTask: () => void
  onGenerateAvatar: () => void
  onAvatarUpload: (file: File) => void
  onOpenDataSettings: () => void
  fileInputRef: React.RefObject<HTMLInputElement>
}

export function AgentContent({
  data,
  state,
  nodeStatus,
  onCreateAgent,
  onToggleAgent,
  onShowSettings,
  onShowLogs,
  onShowUsage,
  onShowCollaboration,
  onShowQdrant,
  onShowScheduling,
  onShowManualTask,
  onGenerateAvatar,
  onAvatarUpload,
  onOpenDataSettings,
  fileInputRef,
}: AgentContentProps) {
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      onAvatarUpload(file)
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <div className="w-full">
      {/* Header with Avatar and Agent Info */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative">          <Avatar className="w-12 h-12">
            <AvatarImage src={data.avatarUrl!} alt={data.name || 'Agent'} />
            <AvatarFallback>
              <Bot className="w-6 h-6" />
            </AvatarFallback>
          </Avatar>
          
          {/* Status indicator */}
          <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${
            nodeStatus === 'success' ? 'bg-green-500' :
            nodeStatus === 'loading' ? 'bg-yellow-500' :
            nodeStatus === 'error' ? 'bg-red-500' : 'bg-gray-400'
          }`} />
        </div>

        <div className="flex-1">
          <div className="font-semibold text-lg">{data.name || 'Agent'}</div>
          <div className="text-sm text-gray-600 capitalize">
            {data.role || 'general'}
          </div>
        </div>        <Badge variant={data.enabled ? 'default' : 'secondary'} className="text-xs">
          {data.enabled ? 'Active' : 'Inactive'}
        </Badge>
      </div>

      {/* Status Message */}
      {data.statusMessage && (
        <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded text-sm">
          {String(data.statusMessage)}
        </div>
      )}

      {/* Agent Command Status */}
      {data.agentCommand?.processing && (
        <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 animate-spin" />
            {data.agentCommand.message || 'Processing...'}
          </div>
        </div>
      )}

      {data.agentCommand?.error && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          Error: {data.agentCommand.error}
        </div>
      )}

      {/* Main Actions */}
      <div className="space-y-2 mb-4">
        {!data.agentId ? (
          <Button onClick={onCreateAgent} className="w-full" size="sm">
            <Plus className="w-4 h-4 mr-2" />
            Create Agent
          </Button>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={onToggleAgent}
                variant={data.enabled ? 'destructive' : 'default'}
                size="sm"
                disabled={nodeStatus === 'loading'}
              >
                {data.enabled ? (
                  <>
                    <Pause className="w-4 h-4 mr-1" />
                    Disable
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-1" />
                    Enable
                  </>
                )}
              </Button>
              
              <Button onClick={onShowSettings} variant="outline" size="sm">
                <SettingsIcon className="w-4 h-4 mr-1" />
                Settings
              </Button>
            </div>

            <Button onClick={onShowManualTask} variant="outline" size="sm" className="w-full">
              <Target className="w-4 h-4 mr-2" />
              Execute Manual Task
            </Button>
          </>
        )}
      </div>

      {/* Avatar Actions */}
      {data.agentId && (
        <div className="mb-4">
          <div className="text-sm font-medium mb-2">Avatar</div>
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={onGenerateAvatar}
              variant="outline"
              size="sm"
              disabled={state.avatarGenerating}
            >
              <Image className="w-4 h-4 mr-1" />
              {state.avatarGenerating ? 'Generating...' : 'Generate'}
            </Button>
            
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
              size="sm"
              disabled={state.avatarUploading}
            >
              <Upload className="w-4 h-4 mr-1" />
              {state.avatarUploading ? 'Uploading...' : 'Upload'}
            </Button>
          </div>
            <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            aria-label="Upload avatar image"
          />
        </div>
      )}

      {/* Feature Buttons */}
      {data.agentId && (
        <div className="space-y-2 mb-4">
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={onShowLogs} variant="outline" size="sm">
              <Activity className="w-4 h-4 mr-1" />
              Logs
            </Button>
            
            <Button onClick={onShowUsage} variant="outline" size="sm">
              <DollarSign className="w-4 h-4 mr-1" />
              Usage
            </Button>
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={onShowCollaboration} variant="outline" size="sm">
              <Users className="w-4 h-4 mr-1" />
              Collab
            </Button>
            
            <Button onClick={onShowQdrant} variant="outline" size="sm">
              <Database className="w-4 h-4 mr-1" />
              Qdrant
            </Button>
          </div>
          
          <Button onClick={onShowScheduling} variant="outline" size="sm" className="w-full">
            <Clock className="w-4 h-4 mr-2" />
            Scheduling
          </Button>
        </div>
      )}

      {/* Stats */}
      {data.agentId && !state.isMinimized && (
        <div className="mb-4">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="text-center">
              <div className="font-semibold">{data.observationList?.length || 0}</div>
              <div className="text-gray-500">Observing</div>
            </div>
            <div className="text-center">
              <div className="font-semibold">{state.agentTasks.length}</div>
              <div className="text-gray-500">Tasks</div>
            </div>
            <div className="text-center">
              <div className="font-semibold">{state.agentLogs.slice(0, 10).length}</div>
              <div className="text-gray-500">Recent</div>
            </div>
          </div>
        </div>
      )}

      {/* Data Settings Button */}
      <Button
        variant="outline"
        size="sm"
        onClick={onOpenDataSettings}
        className="w-full"
      >
        <SettingsIcon className="w-4 h-4 mr-1" />
        Data Settings
      </Button>
    </div>
  )
}
